import { useEffect, useState } from "react";

/**
 * This function allows you to pass in a `value` that comes from a `useState`
 * and it returns a debounced version of that value.
 * What this means is that the value itself will not be updated until `delay`,
 * and if you update the value before `delay` has passed, the timer will reset.
 *
 * This is useful for things like search bars where you don't want to make a request
 * on every keypress, but rather after the user has stopped typing for a bit.
 * @param value The value you want to debounce
 * @param delay The delay in milliseconds
 * @returns The debounced value
 */
export const useDebouncedValue = <T>(value: T, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};
